import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import useCostCenterApi from "../../http/costCenter/api";
import { CostCenter, InsertCostCenter, UpdateCostCenter } from "../../http/costCenter/types";
import { PaginationQuery } from "../../types";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import TH from "../ui/TH";
import CostCenterForm, { CostCenterFormBody } from "./CostCenterForm";

const CostCenters: FC = () => {
  const filters = useListFilters({ isSearchable: true, prefix: "cc" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { isOpen, open, close } = useToggle();
  const [editCostCenter, setEditCostCenter] = useState<CostCenter>();
  const [deleteCostCenter, setDeleteCostCenter] = useState<CostCenter>();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const costCenterApi = useCostCenterApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };

  const { data, error } = costCenterApi.useCostCenters(query);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  const onSubmit = async (formData: CostCenterFormBody) => {
    if (!editCostCenter) {
      const insertData: InsertCostCenter = {
        code: formData.code,
        name: formData.name,
      };

      try {
        await costCenterApi.createCostCenter(insertData);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    } else {
      const updateData: UpdateCostCenter = {
        name: formData.name,
      };

      try {
        await costCenterApi.updateCostCenter(updateData, editCostCenter.code);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    }
  };

  const actions: Action<CostCenter>[] = [
    {
      title: i18n.t.common.edit,
      onClick: (item) => {
        setEditCostCenter(item);
        open();
      },
      icon: SvgEdit,
    },
    {
      title: i18n.t.common.delete,
      onClick: setDeleteCostCenter,
      icon: SvgDelete,
      color: "red",
      isDivider: true,
    },
  ];

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        onClose={close}
        title={!editCostCenter ? i18n.t.costCenters.newCostCenter : i18n.t.costCenters.editCostCenter}
      >
        <CostCenterForm
          costCenter={editCostCenter}
          onSubmit={onSubmit}
          key={isOpen ? editCostCenter?.code ?? "new" : "closed"}
        />
      </Sidebar>

      <Table<CostCenter>
        buttons={{
          main: (
            <Button
              buttonProps={{
                onClick: () => {
                  setEditCostCenter(undefined);
                  open();
                },
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        rowActions={actions}
        onRowClick={(item) => {
          setEditCostCenter(item);
          open();
        }}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.t.common.code}</th>
            <TH order={orderOptions} fieldName="costCenter.name" title={i18n.t.common.name} />
            <th align="left"></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.code}</td>
            <td>{item.name}</td>
          </>
        )}
        items={data?.items}
        total={data?.total}
        error={error}
      />
      {!!deleteCostCenter && (
        <ModalDialog
          onClose={() => setDeleteCostCenter(undefined)}
          title={i18n.t.costCenters.delete}
          isSubmitting={isModalSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setDeleteCostCenter(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                setIsModalSubmitting(true);
                if (!deleteCostCenter) return;
                try {
                  await costCenterApi.deleteCostCenter(deleteCostCenter.code);
                  setIsModalSubmitting(false);
                  setDeleteCostCenter(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.costCenters.questions.delete}
        </ModalDialog>
      )}
    </>
  );
};

export default CostCenters;
